import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";

// Why a given lesson counts as debt — mirrors the backend's strict rule
// (unpaid past lesson counts regardless of attendance). The old copy said
// "nieobecne zajęcia", which read as a system error to anyone who DID attend.
const debtCause = (lesson) => {
  if (lesson.pay_on_site) {
    return { label: "płatność na miejscu", color: "warning" };
  }
  if (lesson.attended === false) {
    return { label: "nieobecność", color: "default" };
  }
  if (lesson.attended === true) {
    return { label: "udział bez karnetu", color: "default" };
  }
  // attended === null: attendance was never recorded, so we do NOT know whether
  // the user showed up. Show no chip rather than guess — guessing "nieobecność"
  // here is the false accusation this whole dialog was rewritten to remove.
  // (Also covers an older payload with no cause fields at all.)
  return null;
};

const UnpaidDebtDialog = ({ open, onClose, debtAmount, debtCount, debtDetails, onPayDebt }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const hasPayOnSite = debtDetails.some((lesson) => lesson.pay_on_site);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: isMobile ? 2 : 3,
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center',
        pt: 3,
        pb: 2,
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" color="error.main">
          🚫 Nieopłacone zajęcia
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <Stack spacing={2.5}>
          <Alert severity="error" icon="⚠️" sx={{ borderRadius: 2 }}>
            <Typography variant="body2" fontWeight="500">
              Masz nieopłacone zajęcia, które już się odbyły. Opłać je, aby
              zapisać się na nowe.
            </Typography>
          </Alert>
          
          <Card elevation={0} sx={{ backgroundColor: '#ffebee', border: '2px solid #ef5350' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Kwota do zapłaty
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error">
                    {debtAmount} PLN
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Liczba zajęć
                  </Typography>
                  <Chip 
                    label={debtCount} 
                    color="error"
                    sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
          
          <Box>
            <Typography variant="body2" fontWeight="600" gutterBottom sx={{ mb: 1.5 }}>
              📋 Szczegóły nieopłaconych zajęć:
            </Typography>
            
            <Stack 
              spacing={1} 
              sx={{ 
                maxHeight: 240, 
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#ccc',
                  borderRadius: '4px',
                },
              }}
            >
              {debtDetails.map((lesson, index) => {
                const cause = debtCause(lesson);
                return (
                  <Card key={index} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {lesson.lesson_name}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              📅 {new Date(lesson.lesson_date).toLocaleDateString("pl-PL")}
                            </Typography>
                            {cause && (
                              <Chip
                                label={cause.label}
                                size="small"
                                color={cause.color}
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <Chip
                          label={`${lesson.price} PLN`}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </Box>
          
          {hasPayOnSite && (
            <Alert severity="warning" icon="💵" sx={{ borderRadius: 2 }}>
              <Typography variant="caption">
                Dla zajęć oznaczonych „płatność na miejscu": jeśli zapłacono
                gotówką w studio, napisz na{" "}
                <strong>{process.env.REACT_APP_CONTACT_EMAIL}</strong> —
                płatność mogła nie zostać odnotowana w systemie.
              </Typography>
            </Alert>
          )}
          <Alert severity="info" icon="💡" sx={{ borderRadius: 2 }}>
            <Typography variant="caption">
              Możesz zapłacić online lub skontaktować się z instruktorem.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        pb: 3, 
        gap: 1,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          fullWidth={isMobile}
          sx={{ minHeight: 44 }}
        >
          Anuluj
        </Button>
        <Button 
          onClick={onPayDebt} 
          color="success" 
          variant="contained"
          fullWidth={isMobile}
          sx={{ minHeight: 44 }}
        >
          Zapłać online ({debtAmount} PLN)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnpaidDebtDialog;
